import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import { getFlatRoutes } from "../utils";
import { usePaneCacheStore } from "./usePaneCacheStore";
import { DEFAULT_ROUTE_ROOT } from "../utils/constants";
import microApp from "@micro-zoe/micro-app";
import { omit } from "lodash-es";
import { sendGlobalMessage } from "./useMicroAppStore";
var namespaceSetting = {
  name: "".concat(process.env.APP_NAME, "_").concat(process.env.APP_VERSION, "_AppStore")
};
var initialState = {
  menusLoading: false,
  menuCollapsed: false,
  menus: [],
  panes: [],
  pathname: DEFAULT_ROUTE_ROOT,
  panesHistory: [],
  flatPathRoutes: {},
  flatPathMenus: {},
  refreshPath: null,
  tabsMode: true,
  microAppContainerRender: void 0,
  layoutBasePath: DEFAULT_ROUTE_ROOT,
  homePath: DEFAULT_ROUTE_ROOT,
  locale: {
    setCollapsed: "收缩",
    setExpanded: "展开",
    home: "首页",
    refresh: "重载当前页面",
    back: "后退",
    closeOthers: "关闭其它标签页",
    closeAll: "关闭全部标签页"
  },
  tabsHandlers: {
    add: function add() {
      console.warn("addTab has not been initialized");
    },
    remove: function remove() {
      console.warn("removeTabByPath has not been initialized");
      return Promise.resolve();
    },
    closeAll: function closeAll() {
      console.warn("closeAllTabs has not been initialized");
    },
    back: function back() {
      console.warn("back has not been initialized");
    }
  },
  pageHandlers: {
    add: function add() {
      console.warn("addPage has not been initialized");
    },
    remove: function remove() {
      console.warn("removePageByPath has not been initialized");
      return Promise.resolve();
    },
    closeAll: function closeAll() {
      console.warn("closeAllTabs has not been initialized");
    },
    back: function back() {
      console.warn("back has not been initialized");
    }
  },
  tabCacheHandlers: {
    getCaches: function getCaches() {
      console.warn("getCaches has not been initialized");
      return [];
    },
    removeCache: function removeCache() {
      console.warn("removeCache has not been initialized");
    },
    cleanAllCache: function cleanAllCache() {
      console.warn("cleanAllCache has not been initialized");
    },
    cleanOtherCache: function cleanOtherCache() {
      console.warn("cleanOtherCache has not been initialized");
    }
  },
  errorCaptureException: function errorCaptureException() {}
};

// create the store
export var useAppStore = createWithEqualityFn()(devtools(persist(function (set, get) {
  return _objectSpread(_objectSpread({}, initialState), {}, {
    setFlatPathRoutes: function setFlatPathRoutes(routes) {
      set({
        flatPathRoutes: getFlatRoutes(routes)
      });
    },
    setMenus: function setMenus(menus) {
      set({
        menus: menus,
        flatPathMenus: getFlatRoutes(menus)
      });
    },
    setPanes: function setPanes(newPanes) {
      set(_objectSpread(_objectSpread({}, get()), {}, {
        panes: _toConsumableArray(newPanes)
      }), true);
    },
    setRefreshPath: function setRefreshPath(path) {
      set({
        refreshPath: path + "@@" + Math.random()
      });
    },
    addHistory: function addHistory(path, search) {
      var panesHistory = get().panesHistory.filter(function (el) {
        return el !== path;
      });
      panesHistory.push("".concat(path).concat(search || ""));
      set({
        panesHistory: panesHistory
      });
    },
    removeHistory: function removeHistory(path, options) {
      var retainPath = options === null || options === void 0 ? void 0 : options.retainPath;
      // 不传，删除所有
      if (!path) {
        set({
          panesHistory: retainPath ? [retainPath] : []
        });
        return get().homePath || DEFAULT_ROUTE_ROOT;
      }
      var newPanesHistory = _toConsumableArray(get().panesHistory).filter(function (pathWithSearch) {
        return pathWithSearch.split("?")[0] !== path;
      });
      set({
        panesHistory: newPanesHistory
      });
      return newPanesHistory[newPanesHistory.length - 1] || get().homePath || DEFAULT_ROUTE_ROOT;
    },
    setMenuCollapsed: function setMenuCollapsed(collapsed) {
      set({
        menuCollapsed: collapsed
      });
      // 向全局推送侧边栏状态
      sendGlobalMessage({
        "@@siderMenuCollapsed": collapsed
      });
    },
    setPageHandlers: function setPageHandlers(fns) {
      set({
        // 暂且兼容
        tabsHandlers: _objectSpread(_objectSpread({}, get().pageHandlers), fns),
        pageHandlers: _objectSpread(_objectSpread({}, get().pageHandlers), fns)
      });
    },
    clearData: function clearData() {
      set({
        menusLoading: false,
        menuCollapsed: false,
        menus: [],
        panes: [],
        panesHistory: [],
        flatPathRoutes: {},
        flatPathMenus: {},
        refreshPath: null,
        homePath: DEFAULT_ROUTE_ROOT
      });
      // 清空滚动条缓存
      usePaneCacheStore.getState().clearScrollHistory();
      // 清空微应用全局数据
      microApp.clearGlobalData();
    },
    setErrorCaptureException: function setErrorCaptureException(errorCaptureException) {
      set({
        errorCaptureException: errorCaptureException
      });
    },
    setPathname: function setPathname(path) {
      set({
        pathname: path || DEFAULT_ROUTE_ROOT
      });
    },
    setTabTitle: function setTabTitle(path, name) {
      var currentPanes = _toConsumableArray(get().panes);
      var willChangedPane = currentPanes.find(function (pane) {
        return pane.path === path;
      });
      if (willChangedPane) {
        willChangedPane.name = name;
        set({
          panes: currentPanes
        });
      }
    }
  });
}, _objectSpread(_objectSpread({}, namespaceSetting), {}, {
  storage: createJSONStorage(function () {
    return sessionStorage;
  }, {
    reviver: function reviver(key, value) {
      if (key === "microApp") {
        if (typeof value === "string") {
          return JSON.parse(value);
        }
        return value;
      }
      return value;
    },
    replacer: function replacer(key, value) {
      if (key === "microApp") {
        return JSON.stringify(value);
      }
      return value;
    }
  }),
  partialize: function partialize(state) {
    // 子应用不缓存面板数据
    if (window.__MICRO_APP_ENVIRONMENT__) {
      return {};
    }
    return omit(state, ["errorCaptureException", "locale"]);
  }
})), namespaceSetting), shallow);
export var routesArraySelector = function routesArraySelector(state) {
  return Object.entries(state.flatPathRoutes).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      value = _ref2[1];
    return value;
  });
};
export var microAppPathsArraySelector = function microAppPathsArraySelector(state) {
  return Object.entries(state.flatPathRoutes).map(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      value = _ref4[1];
    if (value.microAppPath) {
      return _objectSpread(_objectSpread({}, value), {}, {
        originPath: value.path,
        path: value.microAppPath
      });
    }
    return false;
  }).filter(function (value) {
    return value;
  });
};
export var menusArraySelector = function menusArraySelector(state) {
  return Object.entries(state.flatPathMenus).map(function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
      value = _ref6[1];
    return value;
  });
};